import {
    Async,
    Deferred,
    eachArray,
    GoogleMap,
    Instance,
    Objekt,
    Promize,
} from '@siposdani87/sui-js';
import { app } from '../app';
import { ChamberService } from '@services/chamberService';
import { MapService } from '@services/mapService';
import { OrganizationService } from '@services/organizationService';
import { DashboardController } from './dashboardController';

export class ChambersShowController extends DashboardController {
    static readonly inject = [
        'instances',
        'chamberService',
        'organizationService',
        'mapService',
    ] as const;

    chamberId!: string;
    chamber!: Objekt;
    organizations!: Objekt[];
    map!: GoogleMap;

    constructor(
        instances: Instance,
        private chamberService: ChamberService,
        private organizationService: OrganizationService,
        private mapService: MapService,
    ) {
        super(instances);
    }

    protected override _initLayout(): void {
        this.chamberId = this.state.getParam('chamberId');
        this.chamber = new Objekt();

        this._initToolbarButtons();

        this._drawContent();
    }

    private _initToolbarButtons(): void {
        this.helper.iconButton('.btn-chambers-index', this.dom, () => {
            this.state.goBack('chambers.index');
        });
    }

    protected override _drawContent(): Promize | boolean {
        this._initMap();
        return true;
    }

    private _loadChamber(): Promize {
        const deferred = new Deferred();
        this.chamberService
            .get(this.chamberId, {
                fields: 'id,name,country.location',
            })
            .then(
                (response) => {
                    this.chamber = response.get<Objekt>('chamber');
                    deferred.resolve();
                },
                () => {
                    deferred.reject();
                },
            );
        return deferred.promise();
    }

    private _loadOrganizations(): Promize {
        const deferred = new Deferred();
        this.organizationService
            .getAllByChamber(this.chamberId, {
                fields: 'id,name,areas.locations',
            })
            .then(
                (response) => {
                    this.organizations = response.get<Objekt[]>('organizations');
                    deferred.resolve();
                },
                () => {
                    deferred.reject();
                },
            );
        return deferred.promise();
    }

    private _initMap(): void {
        if (!this.map) {
            this.map = this.mapService.createGoogleMap(this.dom, '.map', {
                mapTypeId: 'custom',
                mapTypeControlOptions: {
                    mapTypeIds: ['custom', google.maps.MapTypeId.SATELLITE],
                },
                mapTypeControl: true,
                scrollwheel: true,
            });

            this.map.setPolygons(this.mapService.getAreaPolygonOptions());
        }

        const async = new Async();
        async
            .parallel([
                this._loadChamber.bind(this),
                this._loadOrganizations.bind(this),
            ])
            .then(() => {
                this._setMap();
            });
    }

    private _setMap(): void {
        eachArray(this.organizations, (organization) => {
            const areas = organization.get<Objekt[]>('areas', []);
            eachArray(areas, (area) => {
                const points = area.get<Array<{ latitude: number; longitude: number }>>('locations', []);
                this.map.createPolygon(
                    organization.get<string>('id'),
                    organization.get<string>('name'),
                    points,
                );
            });
        });

        this.map.setCenter(
            this.chamber.get<number>('country.location.latitude'),
            this.chamber.get<number>('country.location.longitude'),
        );
    }

    exit(): void {
        super.exit();

        this.mapService.removeGoogleMap(this.map);
    }
}

export const chambersShowController = app.controller(
    'chambersShowController',
    ChambersShowController,
);
